import React, { useState } from 'react';
import { Brain, AlertTriangle, Settings, Target, Activity } from 'lucide-react';

const AnomalyDetectionPanel = () => {
  const [sensitivity, setSensitivity] = useState(75);
  const [detectionRules, setDetectionRules] = useState([
    {
      id: 1,
      name: 'Unusual Transaction Volume',
      enabled: true,
      threshold: '±30% from baseline',
      confidence: 94,
      detections: 12
    },
    {
      id: 2,
      name: 'Settlement Amount Mismatch',
      enabled: true,
      threshold: '>$100 variance',
      confidence: 98,
      detections: 8
    },
    {
      id: 3,
      name: 'Timing Anomaly Detection',
      enabled: true,
      threshold: '>2 hours delay',
      confidence: 87,
      detections: 5
    },
    {
      id: 4,
      name: 'Pattern Deviation Analysis',
      enabled: false,
      threshold: 'ML-based scoring',
      confidence: 91,
      detections: 3
    }
  ]);

  const [recentAnomalies, setRecentAnomalies] = useState([
    {
      id: 1,
      type: 'Volume Spike',
      severity: 'high',
      confidence: 96,
      description: 'Transaction volume 45% above baseline',
      timestamp: '2026-02-04T03:15:00Z',
      status: 'investigating'
    },
    {
      id: 2,
      type: 'Amount Variance',
      severity: 'medium',
      confidence: 89,
      description: 'Settlement amount differs by $234.50',
      timestamp: '2026-02-04T02:45:00Z',
      status: 'auto-resolved'
    },
    {
      id: 3,
      type: 'Timing Delay',
      severity: 'low',
      confidence: 82,
      description: 'Settlement delayed by 3.2 hours',
      timestamp: '2026-02-04T01:30:00Z',
      status: 'resolved'
    }
  ]);

  const toggleRule = (ruleId) => {
    setDetectionRules(rules =>
      rules?.map(rule =>
        rule?.id === ruleId ? { ...rule, enabled: !rule?.enabled } : rule
      )
    );
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'low':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'investigating':
        return 'bg-blue-100 text-blue-700';
      case 'auto-resolved':
        return 'bg-green-100 text-green-700';
      case 'resolved':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* ML Model Status */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Brain className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">ML Anomaly Detection Engine</h3>
              <p className="text-sm text-gray-600">Statistical analysis & pattern recognition active</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </div>
        </div>

        {/* Sensitivity Slider */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Detection Sensitivity
            </label>
            <span className="text-sm font-semibold text-purple-600">{sensitivity}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={sensitivity}
            onChange={(e) => setSensitivity(Number(e?.target?.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Low</span>
            <span>Medium</span>
            <span>High</span>
          </div>
        </div>
      </div>
      {/* Detection Rules */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Detection Rules
        </h3>
        <div className="space-y-2">
          {detectionRules?.map((rule) => (
            <div
              key={rule?.id}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      onClick={() => toggleRule(rule?.id)}
                      className={`w-10 h-6 rounded-full transition-colors ${
                        rule?.enabled ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        rule?.enabled ? 'translate-x-5' : 'translate-x-1'
                      }`} />
                    </button>
                    <h4 className="font-semibold text-gray-900">{rule?.name}</h4>
                  </div>
                  <div className="ml-13 space-y-1">
                    <div className="text-sm text-gray-600">
                      Threshold: <span className="font-medium">{rule?.threshold}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-600">
                        Confidence: <span className="font-semibold text-green-600">{rule?.confidence}%</span>
                      </span>
                      <span className="text-gray-600">
                        Detections: <span className="font-semibold text-blue-600">{rule?.detections}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Anomalies */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Recent Anomalies Detected
        </h3>
        <div className="space-y-3">
          {recentAnomalies?.map((anomaly) => (
            <div
              key={anomaly?.id}
              className="bg-white rounded-lg border border-gray-200 p-4"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(anomaly?.severity)}`}>
                    {anomaly?.type}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(anomaly?.status)}`}>
                    {anomaly?.status?.replace('-', ' ')}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(anomaly.timestamp)?.toLocaleString()}
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-2">{anomaly?.description}</p>
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-600">
                  Confidence Score: <span className="font-semibold">{anomaly?.confidence}%</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnomalyDetectionPanel;